<script lang="ts">
  // A rich card, drawn as our own widgets.
  //
  // What used to happen here — sanitize to a string, hand it to a sandboxed frame, and
  // measure it with a raw-HTML injection — is gone. A card can now carry working
  // controls, and a control cannot live inside a frame's table cell. Building the tree
  // ourselves removes the frame AND the injection, so this version has fewer ways in
  // than the one it replaces, not more.
  //
  // The markup says where a control goes. It has no way to say what one does: that comes
  // from the element's `controls` list, whose kinds are a closed set, and the wording on
  // every button is drawn from our own translations.

  import { t } from '$lib/i18n/index.svelte';
  import { defang } from '$lib/protocol/link-guard';
  import { parseHtmlCard } from '$lib/protocol/sanitize-html';
  import type { HtmlElementCard } from '$lib/protocol/types';

  import CardNodes from './CardNodes.svelte';
  import type { ElementContext } from './context';
  import { CardState } from './controls/card-state.svelte';

  let { element, ctx }: { element: HtmlElementCard; ctx: ElementContext } = $props();

  const nodes = $derived(parseHtmlCard(element.html));

  // Quantities live with the card and die with it. Nothing is persisted: a basket
  // half-filled out of sight and submitted later is the failure this design exists to
  // prevent.
  const card = new CardState();
</script>

{#if nodes.length === 0}
  <p class="fallback">
    {element.fallbackText !== undefined ? defang(element.fallbackText) : t('el.unknown')}
  </p>
{:else}
  <div class="card">
    <CardNodes {nodes} controls={element.controls} {ctx} {card} />
  </div>
{/if}

<style>
  .card {
    font-size: 14px;
    overflow-x: auto;
  }
  .card :global(table) {
    border-collapse: collapse;
  }
  .card :global(td),
  .card :global(th) {
    border: 1px solid var(--line);
    padding: 4px 8px;
    text-align: left;
  }
  .card :global(h1) {
    font-size: 18px;
  }
  .card :global(h2) {
    font-size: 16px;
  }
  .card :global(h3) {
    font-size: 15px;
  }
  .card :global(p) {
    margin: 6px 0;
  }
  .card :global(ul),
  .card :global(ol) {
    margin: 6px 0;
    padding-left: 20px;
  }
  .fallback {
    margin: 0;
    font-style: italic;
    color: var(--ink-soft);
  }
</style>
